import type { User } from '@shared/types';

const API_BASE =
  import.meta.env.VITE_API_URL ||
  (import.meta.env.DEV ? 'http://localhost:8787' : 'https://api.yhgameverse.win');
const TOKEN_KEY = 'auth_token';

class ApiService {
  private getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  private setToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
  }

  private clearToken(): void {
    localStorage.removeItem(TOKEN_KEY);
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = this.getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Add Authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      credentials: 'include',
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || `Request failed: ${response.status}`);
    }

    return response.json();
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      // If we don't have a token, get one first
      if (!this.getToken()) {
        await this.refreshToken();
      }

      const data = await this.request<{ user: User }>('/auth/me');
      return data.user;
    } catch {
      // If auth fails, try to get a new token
      this.clearToken();
      try {
        await this.refreshToken();
        const data = await this.request<{ user: User }>('/auth/me');
        return data.user;
      } catch {
        // In development mode without Cloudflare Access, use a mock user
        if (import.meta.env.DEV) {
          return await this.createMockUser();
        }
        return null;
      }
    }
  }

  async getAccessIdentity(): Promise<{ email: string; name: string } | null> {
    try {
      // Cloudflare Access provides user identity at this endpoint
      const response = await fetch('/cdn-cgi/access/get-identity', {
        credentials: 'include',
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      return {
        email: data.email,
        name: data.name || data.email.split('@')[0],
      };
    } catch (error) {
      console.error('Failed to get Access identity:', error);
      return null;
    }
  }

  async createMockUser(): Promise<User> {
    // Create a mock user for local development
    const mockEmail = 'dev@localhost.local';
    const mockName = 'Dev User';

    try {
      // Try to get a token from the backend
      const response = await fetch(`${API_BASE}/auth/token`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: mockEmail,
          name: mockName,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        this.setToken(data.token);
        return data.user;
      }
    } catch (error) {
      console.error('Failed to create mock user token:', error);
    }

    // If backend token creation fails, return a mock user anyway
    return {
      id: mockEmail,
      email: mockEmail,
      name: mockName,
      picture: `https://ui-avatars.com/api/?name=${encodeURIComponent(mockName)}&background=random`,
      createdAt: Date.now(),
      lastLogin: Date.now(),
    };
  }

  async refreshToken(): Promise<void> {
    try {
      // Get user info from Cloudflare Access
      const identity = await this.getAccessIdentity();

      if (!identity) {
        throw new Error('No Cloudflare Access identity found');
      }

      // Exchange it for a JWT token from our API
      const response = await fetch(`${API_BASE}/auth/token`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: identity.email,
          name: identity.name,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get token');
      }

      const data = await response.json();
      this.setToken(data.token);
    } catch (error) {
      console.error('Failed to refresh token:', error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    await this.request('/auth/logout');
  }

  async createRoom(): Promise<{ roomId: string; roomCode: string }> {
    return this.request('/api/room/create', {
      method: 'POST',
    });
  }

  async joinRoom(code: string): Promise<{ roomId: string }> {
    return this.request(`/api/room/join/${code}`);
  }

  async getAvailableDecks(): Promise<{
    decks: Array<{
      id: string;
      name: string;
      description: string;
      wordCount: number;
    }>;
  }> {
    return this.request('/api/decks');
  }

  async getDebugState(roomId: string): Promise<Record<string, unknown>> {
    if (!import.meta.env.DEV) {
      throw new Error('Debug endpoint only available in development');
    }
    return this.request(`/api/debug/room/${roomId}`);
  }
}

export default new ApiService();
